<template>
	<div>
		<!-- no-filter: Google Places already server-filters; Vuetify's default client filter drops valid matches (LSP-15394). -->
		<v-autocomplete
			ref="autocompleteRef"
			v-model="modelValue"
			v-bind="$attrs"
			:items="items"
			:loading="isLoading || isGeocodingManual"
			:search="searchTerm"
			:hide-no-data="!searchTerm || searchTerm.trim().length === 0"
			@update:search="onSearchInput"
			@update:modelValue="selectPrediction"
			@blur="handleBlur"
			item-title="formattedAddress"
			item-value="placeId"
			:item-prop="true"
			no-filter
			return-object>
			<!-- Bold the trailing (Required)/(Optional) tag on the label (LSP-16777).
			     Label arrives via $attrs; render through FieldLabel unless a caller supplies its own #label. -->
			<template v-if="$attrs.label && !$slots.label" #label>
				<FieldLabel :label="String($attrs.label)" />
			</template>
			<template v-for="(_, slot) in filteredSlots" #[slot]="scope" :key="slot">
				<slot :name="slot" v-bind="scope || {}"></slot>
			</template>
			<template #no-data>
				<v-list-item v-if="isLoading" class="pl-2">
					<v-list-item-title class="text-medium-emphasis">Loading addresses...</v-list-item-title>
				</v-list-item>
				<div v-else-if="searchTerm?.trim()" class="d-flex align-center justify-center no-data-slot">
					<v-btn
						class="elevation-0 pa-2 text-none create-contact-btn create-action-btn"
						color="transparent"
						@mousedown.prevent
						@click="onCreateManualAddress(searchTerm)">
						Create new address '<span class="create-action-query">{{ searchTerm }}</span
						>'
					</v-btn>
				</div>
				<v-list-item v-else class="pl-2">
					<v-list-item-title>No addresses found.</v-list-item-title>
				</v-list-item>
			</template>
		</v-autocomplete>
	</div>
</template>

<script setup lang="ts">
	import { computed, nextTick, toRef, useSlots, useTemplateRef, watch } from "vue";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { useGooglePlacePredictions } from "@/composables/useGooglePlacePredictions";
	import type { GeoAddress, UserLocation } from "@/helpers/location";

	defineOptions({
		inheritAttrs: false
	});

	const modelValue = defineModel<GeoAddress | null>();
	const props = defineProps<{
		locationBias?: UserLocation | null;
		preservePredictionLabel?: boolean;
	}>();
	const autocompleteRef = useTemplateRef("autocompleteRef");
	const emits = defineEmits<{
		(e: "place-changed", geoAddress: GeoAddress | null): void; // Keeping this to avoid making extra breaking changes.
	}>();

	const slots = useSlots();
	const filteredSlots = computed(() => {
		const result = { ...slots };
		delete result["no-data"];
		return result;
	});

	const {
		modelValue: predictionModelValue,
		items,
		isLoading,
		isGeocodingManual,
		userEditedText,
		searchTerm,
		setSearchTermSilent,
		onSearchInput,
		fetchPlacesBySearchTerm,
		handleBlur,
		commitPendingInput,
		selectPrediction,
		createManualAddress
	} = useGooglePlacePredictions({
		locationBias: toRef(props, "locationBias"),
		preservePredictionLabel: props.preservePredictionLabel,
		onPlaceChanged: (geoAddress) => emits("place-changed", geoAddress)
	});

	async function onCreateManualAddress(manualSearchTerm?: string): Promise<void> {
		await createManualAddress(manualSearchTerm);
		autocompleteRef.value?.blur();
	}

	watch(
		modelValue,
		(geoAddress) => {
			predictionModelValue.value = geoAddress;
			if (geoAddress) {
				setSearchTermSilent(geoAddress.formattedAddress);
			} else {
				onSearchInput("");
			}
		},
		{ immediate: true }
	);

	watch(predictionModelValue, (geoAddress) => {
		if (geoAddress !== modelValue.value) {
			modelValue.value = geoAddress ?? null;
		}
	});

	// This exists to satisfy some methods that need to use the autocomplete as a text field.
	const searchStringValue = (value: string) => {
		autocompleteRef.value!.focus();
		nextTick(() => {
			const input = autocompleteRef.value!.$el.querySelector("input");
			if (input) {
				input.value = value;
				input.dispatchEvent(new Event("input"), { bubbles: true });
			}
		});
	};

	defineExpose({
		fetchPlacesBySearchTerm,
		searchStringValue: searchStringValue,
		userEditedText,
		handleBlur,
		commitPendingInput
	});
</script>
<style scoped>
	:deep(input:-webkit-autofill),
	:deep(input:-webkit-autofill:hover),
	:deep(input:-webkit-autofill:focus) {
		-webkit-box-shadow: 0 0 0 1000px white inset;
		-webkit-text-fill-color: inherit;
	}
</style>
